import { css } from '@emotion/react';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { AngularContext, AngularServicesContext } from 'react-app';
import { CourseTimelineContext } from 'timelines/components/course_home/course-timeline';
import useAngularExpression from 'shared/hooks/use-angular-expression';
import { useAppDispatch } from 'redux/store';
import { CombinedCourse, RootState } from 'redux/schemas';
import { Enrollment } from 'redux/schemas/models/my-account';
import { getCourseAliases, getCurrentCourse } from 'redux/selectors/course';
import { CompletionCriteriaType } from 'redux/schemas/app/timeline';
import { resetPortableCompletionPanelData, selectPortableCompletionPanelTab } from 'redux/actions/timeline';

import prodPathReplace from 'shared/prod-path-rewrite';
import {
  black,
  gray2,
  gray6,
  gray7,
  success,
  white,
} from 'styles/global_defaults/colors';
import {
  cardIconSize, doubleSpacing, halfSpacing, largeSpacing, quarterSpacing, standardSpacing,
  threeQuartersSpacing, tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import useCompletionCriteria from '../use-completion-criteria';
import PanelGauges from './panel-gauges';
import PanelContent from './panel-content';

const l1NavHeight = tripleSpacing - 5; // 5px for the top course/institution border

enum UserCourseCompletionStatus {
  NOT_COMPLETED = 0,
  AUTO = 1,
  MANUAL = 2,
}

const usePortableCompletionHeader = () => {
  const { isLearnerProgressModal, courseCompletion } = useContext(CourseTimelineContext) || {};

  // Completion and SOA
  const { injectServices } = useContext(AngularContext);
  const [$state, CourseHomeManager] = injectServices(['$state', 'CourseHomeManager']);
  const showSoa: boolean = $state.current.name === 'course-home'
    && CourseHomeManager?.courseHome?.showStatementOfAccomplishment;
  const { isCompleted: isRequirementsCompleted } = useCompletionCriteria();

  const currentCourse: CombinedCourse = useSelector(getCurrentCourse);
  const userCourse: Enrollment = useSelector((state: RootState) => state.models.enrollments[currentCourse.userCourse]);
  const { completionStatus: currentUserCompletionStatus } = userCourse; // 0 - Not completed, 1 - Auto, 2 - Manual

  let isCourseCompleted: boolean = !!currentUserCompletionStatus;
  let courseCompletionStatus: UserCourseCompletionStatus = currentUserCompletionStatus;
  let courseCompletionProgress: string = null;

  if (isLearnerProgressModal) {
    isCourseCompleted = courseCompletion.status > 0 || courseCompletion.progress === 'completed';
    courseCompletionStatus = courseCompletion.status;
    courseCompletionProgress = courseCompletion.progress;
  }

  return {
    showSoa,
    isRequirementsCompleted,
    isCourseCompleted,
    courseCompletionStatus,
    courseCompletionProgress,
    currentCourse,
  };
};

const PortableCompletionTitle = () => {
  const { isLearnerProgressModal, isFullTimeline } = useContext(CourseTimelineContext) || {};

  const {
    showSoa,
    isRequirementsCompleted,
    courseCompletionStatus,
    courseCompletionProgress,
    currentCourse,
  } = usePortableCompletionHeader();

  const { enabledFactorCount } = useCompletionCriteria();

  // Progress text should visible in lecture page whether tasks is completed or not
  // But it is not displayed in course-home
  const showProgress = !showSoa // Show SOA as per course home response
    // Requirements is completed or this is happening in lecture page
    && (!isRequirementsCompleted || !isFullTimeline)
    // Course completion is not completed
    && courseCompletionProgress !== 'completed'
    // Course status is not manual. If it is manually completed, no need to display
    // this title
    && courseCompletionStatus !== UserCourseCompletionStatus.MANUAL
    // In Archive mode this is not displayed. But if it is learner progress modal
    // it will display even if the course is in archive mode
    && (!currentCourse.inArchiveMode || isLearnerProgressModal);

  return (
    <React.Fragment>
      {showProgress && (
        <div className={`pb-3 text-center ${isFullTimeline && enabledFactorCount > 1 ? 'card-subtitle' : 'card-title'}`}>
          { t.COMPLETION_CRITERIA.PROGRESS_TOWARDS_COURSE_COMPLETION() }
        </div>
      )}
    </React.Fragment>
  );
};

const PortableCompletionSOA = () => {
  const { isLearnerProgressModal } = useContext(CourseTimelineContext) || {};

  const {
    showSoa,
    isRequirementsCompleted,
    isCourseCompleted,
    currentCourse,
  } = usePortableCompletionHeader();

  const { courseAliases } = useSelector((state) => getCourseAliases(state));
  const userCourse = useSelector((state) => state.models.enrollments[currentCourse?.userCourse]);

  const catalogId = currentCourse?.catalogId;
  const userId = userCourse?.userId;
  const { injectServices } = useContext(AngularContext);
  const [$state, CourseHomeManager] = injectServices(['$state', 'CourseHomeManager']);

  let selectCase = 'both';
  let credlyLink = '';
  let soaLink = '';
  const soaText = t.COURSE_ADMIN_MEGA_MENU.STATEMENT_OF_ACCOMPLISHMENT.LABEL();
  const credlyBadgeEnabled = isCourseCompleted && !!(CourseHomeManager?.courseHome?.credlyBadge);

  if (credlyBadgeEnabled) {
    selectCase = 'credly';
    credlyLink = CourseHomeManager?.courseHome?.credlyBadge;
  }

  if (showSoa) {
    selectCase = credlyBadgeEnabled ? 'both' : 'soa';
    soaLink = $state.href('statement-modal', { catalogId, userId });
  }

  const showCertificate = (showSoa || !!credlyBadgeEnabled) && !isLearnerProgressModal;

  // For Learner progress modal, we will display the portable completion anyway
  const isArchivedCourse = currentCourse.inArchiveMode && !isLearnerProgressModal;
  const takeAwayLink = $state.href('course-my-take-away', { catalogId });

  return (
    <React.Fragment>
      { (showSoa || isCourseCompleted || isRequirementsCompleted) && (
        <div className='pt-2 d-flex align-items-center row congrats-container'>
          <div className='col-md-4'>
            <img
              className='congrats-image'
              src={prodPathReplace('images/congratulations.png')}
              alt='Certification of Completion Badge'
            />
          </div>
          <div className='col-md-8 ml-2 d-flex flex-column'>
            <div className='page-subtitle'>{t.COURSE_HOME.STATEMENT_OF_ACCOMPLISHMENT.HEADER()}</div>
            <div className='blue-links mt-2'>
              {
                (!!showCertificate) && (
                  <div>
                    {t.COURSE_HOME.STATEMENT_OF_ACCOMPLISHMENT.VIEWABLE_BODY({
                      selectCase,
                      credlyLink,
                      soaLink,
                      courseAlias: courseAliases.courseAlias,
                      soaText,
                    })}
                  </div>
                )
              }
              {
                (!showCertificate && (isRequirementsCompleted || isCourseCompleted)) && (
                  <div>
                    {t.COMPLETION_CRITERIA.FINISHED_ALL_REQUIREMENTS({
                      courseAlias: courseAliases.CourseAlias,
                    })}
                  </div>
                )
              }
              {
                (isArchivedCourse) && (
                  <div className='mt-2'>
                    {t.COURSE_HOME.ARCHIVE_MODE.BODY({
                      courseName: currentCourse.name,
                      courseAlias: courseAliases.courseAlias,
                    })}
                  </div>
                )
              }
            </div>
          </div>
        </div>
      )}
      {/* Archive mode and shown if learner hasn't completed course */}
      {(isArchivedCourse && !showSoa && !isCourseCompleted) && (
        <div className='archived-not-completed section d-flex'>
          <i className='icon icon-success' />
          <div className='d-flex align-items-center ml-4'>{t.COURSE_HOME.ARCHIVE_MODE.BODY({
            courseName: currentCourse.name,
            courseAlias: courseAliases.courseAlias,
          })}
          </div>
        </div>
      )}

      {(!isArchivedCourse && !isLearnerProgressModal && (isRequirementsCompleted || isCourseCompleted)) && (
        <div className='take-away-cta-card d-flex align-items-center'>
          <div className='take-away-visual d-flex align-items-center justify-content-center'>
            <span className='take-away-visual-icon'>
              <i className='icon icon-gen-ai' />
            </span>
          </div>
          <div className='take-away-divider' />
          <div className='take-away-content'>
            <div className='take-away-top d-flex align-items-start justify-content-between'>
              <div>
                <div className='take-away-title'>{t.COURSE_HOME.TAKE_AWAY.HIGHLIGHTS_TITLE()}</div>
                <div className='take-away-subtitle'>{t.COURSE_HOME.TAKE_AWAY.HIGHLIGHTS_SUBTITLE()}</div>
              </div>
              <a
                className='take-away-button'
                href={takeAwayLink}
                target='_blank'
                rel='noopener noreferrer'
              >
                <i className='icon icon-gen-ai' />
                {t.COURSE_HOME.TAKE_AWAY.VIEW_TAKEAWAYS()}
              </a>
            </div>
            <div className='take-away-highlights d-flex align-items-center'>
              <div className='take-away-highlight take-away-highlight-skills d-flex align-items-center'>
                <span className='take-away-highlight-icon'>
                  <i className='icon icon-success' />
                </span>
                <div>
                  <div className='take-away-highlight-title'>{t.COURSE_HOME.TAKE_AWAY.NEW_SKILLS()}</div>
                  <div className='take-away-highlight-caption'>{t.COURSE_HOME.TAKE_AWAY.ACQUIRED()}</div>
                </div>
              </div>
              <div className='take-away-highlight-separator' />
              <div className='take-away-highlight take-away-highlight-milestones d-flex align-items-center'>
                <span className='take-away-highlight-icon'>
                  <i className='icon icon-badge' />
                </span>
                <div>
                  <div className='take-away-highlight-title'>{t.COURSE_HOME.TAKE_AWAY.MILESTONES()}</div>
                  <div className='take-away-highlight-caption'>{t.COURSE_HOME.TAKE_AWAY.ACHIEVED()}</div>
                </div>
              </div>
              <div className='take-away-highlight-separator' />
              <div className='take-away-highlight take-away-highlight-strengths d-flex align-items-center'>
                <span className='take-away-highlight-icon'>
                  <i className='icon icon-goal' />
                </span>
                <div>
                  <div className='take-away-highlight-title'>{t.COURSE_HOME.TAKE_AWAY.TOP_STRENGTHS()}</div>
                  <div className='take-away-highlight-caption'>{t.COURSE_HOME.TAKE_AWAY.IDENTIFIED()}</div>
                </div>
              </div>
              <div className='take-away-highlight-separator' />
              <div className='take-away-highlight take-away-highlight-insights d-flex align-items-center'>
                <span className='take-away-highlight-icon'>
                  <i className='icon icon-ideas' />
                </span>
                <div>
                  <div className='take-away-highlight-title'>{t.COURSE_HOME.TAKE_AWAY.KEY_INSIGHTS()}</div>
                  <div className='take-away-highlight-caption'>{t.COURSE_HOME.TAKE_AWAY.UNLOCKED()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

const loaderStyles = css`
  padding-bottom: ${standardSpacing}px;

  @keyframes shineAnimation {
    0% {
      background-position: 0% 50%;
    }

    100% {
      background-position: -100% 50%;
    }
  }

  .loading {
    background: linear-gradient(90deg, ${gray6} 35%, ${gray7} 45%, ${gray6} 55%);
    background-size: 600% 600%;
    animation: shineAnimation 1.5s infinite;
  }

  .title-bar {
    width: 60%;
    height: ${halfSpacing}px;
    margin-bottom: ${doubleSpacing}px;
    margin-left: 20%;
  }

  .placeholder-bar {
    width: 100%;
    height: ${quarterSpacing}px;
    margin: ${threeQuartersSpacing}px auto;
  }

  .short-bar {
    margin-left: 0;
    margin-right: 0;
    width: 80%;
  }
`;

const CompletionLoader = () => (
  <div css={loaderStyles}>
    <div className='loading title-bar' />
    <div className='loading placeholder-bar' />
    <div className='loading placeholder-bar' />
    <div className='loading placeholder-bar short-bar' />
  </div>
);

const headerStyles = (isFullTimeline) => css`
  &.completion-criteria-panel-header {
    background: linear-gradient(to right, rgba(247,251,232, 0.3), rgba(220, 253, 253, 0.3));
    border-bottom: 1px solid ${gray6};
    padding: ${threeQuartersSpacing}px ${3 * standardSpacing}px ${largeSpacing}px;

    .archived-not-completed {
      .icon-success {
        font-size: ${doubleSpacing}px;
        color: ${success}
      }
    }

    .congrats-container {
      height: 90px;
    }
    ${isFullTimeline && css`
      .congrats-container {
        height: 110px;
        padding-bottom: ${standardSpacing}px;
      }
    `}

    .congrats-image {
      height: ${cardIconSize}px;
      width: ${cardIconSize}px;
      float: right;
    }

    .take-away-cta-card {
      background: ${white};
      border: 1px solid #c7ebe5;
      border-radius: ${standardSpacing}px;
      box-shadow: 0 2px 8px rgba(58, 66, 77, 0.08);
      margin-top: ${standardSpacing}px;
      padding: ${standardSpacing}px ${largeSpacing}px;
      gap: ${standardSpacing}px;

      .take-away-visual {
        flex: 0 0 120px;
        min-height: 130px;
      }

      .take-away-visual-icon {
        align-items: center;
        background: #e9faf3;
        border-radius: 50%;
        color: #10a57a;
        display: inline-flex;
        font-size: 36px;
        height: 92px;
        justify-content: center;
        width: 92px;
      }

      .take-away-divider {
        align-self: stretch;
        background: #dfe7f0;
        flex: 0 0 1px;
      }

      .take-away-content {
        flex: 1;
        min-width: 0;
      }

      .take-away-top {
        gap: ${standardSpacing}px;
      }

      .take-away-title {
        color: ${black};
        font-size: 22px;
        font-weight: 600;
        line-height: 1.2;
      }

      .take-away-subtitle {
        color: ${gray2};
        font-size: 14px;
        margin-top: ${quarterSpacing}px;
      }

      .take-away-highlights {
        border-top: 1px solid #dfe7f0;
        gap: ${standardSpacing}px;
        margin-top: ${standardSpacing}px;
        padding-top: ${standardSpacing}px;
      }

      .take-away-highlight {
        flex: 1;
        gap: ${halfSpacing}px;
      }

      .take-away-highlight-icon {
        align-items: center;
        border-radius: 50%;
        display: inline-flex;
        font-size: 18px;
        height: 44px;
        justify-content: center;
        width: 44px;
      }

      .take-away-highlight-skills {
        .take-away-highlight-icon {
          background: #e7f7ed;
          color: #0c9c68;
        }
      }

      .take-away-highlight-milestones {
        .take-away-highlight-icon {
          background: #e9f1ff;
          color: #1d56c9;
        }
      }

      .take-away-highlight-strengths {
        .take-away-highlight-icon {
          background: #f2ecff;
          color: #6d3bdf;
        }
      }

      .take-away-highlight-insights {
        .take-away-highlight-icon {
          background: #fff3df;
          color: #d19014;
        }
      }

      .take-away-highlight-separator {
        align-self: stretch;
        background: #dfe7f0;
        flex: 0 0 1px;
      }

      .take-away-highlight-title {
        font-size: 14px;
        font-weight: 600;
        line-height: 1.25;
      }

      .take-away-highlight-caption {
        color: ${gray2};
        font-size: 12px;
        margin-top: 2px;
      }

      .take-away-highlight-skills .take-away-highlight-title {
        color: #0c9c68;
      }

      .take-away-highlight-milestones .take-away-highlight-title {
        color: #1d56c9;
      }

      .take-away-highlight-strengths .take-away-highlight-title {
        color: #6d3bdf;
      }

      .take-away-highlight-insights .take-away-highlight-title {
        color: #d19014;
      }

      .take-away-button {
        align-items: center;
        background: ${white};
        border: 2px solid #08c6b1;
        border-radius: 999px;
        color: #188f84;
        display: inline-flex;
        font-size: 16px;
        font-weight: 600;
        gap: ${halfSpacing}px;
        justify-content: center;
        min-height: 48px;
        padding: ${halfSpacing}px ${standardSpacing}px;
        text-decoration: none;
        white-space: nowrap;

        i {
          color: #08c6b1;
          font-size: 16px;
        }

        &:hover,
        &:focus {
          color: #188f84;
          text-decoration: none;
        }
      }

      @media (max-width: 991px) {
        align-items: flex-start;
        flex-direction: column;

        .take-away-visual {
          min-height: auto;
        }

        .take-away-divider {
          display: none;
        }

        .take-away-top {
          align-items: flex-start;
          flex-direction: column;
        }

        .take-away-title {
          font-size: 20px;
        }

        .take-away-subtitle {
          font-size: 14px;
        }

        .take-away-highlights {
          align-items: stretch;
          flex-direction: column;
          gap: ${halfSpacing}px;
        }

        .take-away-highlight-separator {
          display: none;
        }
      }
    }
  }
`;

export const PortableCompletionHeader = () => {
  const { isLearnerProgressModal, isFullTimeline } = useContext(CourseTimelineContext) || {};

  const { currentCourse } = usePortableCompletionHeader();

  const isCourseCompletionProgressLoading = useSelector((state: RootState) => state.app.timeline.isCourseCompletionProgressLoading);

  const showGauges = isLearnerProgressModal || !currentCourse.inArchiveMode;

  return (
    <div css={headerStyles(isFullTimeline)} className='completion-criteria-panel-header'>
      <div className='body-text-wrapper'>
        {isCourseCompletionProgressLoading
          ? (
            <CompletionLoader />
          )
          : (
            <React.Fragment>
              <PortableCompletionTitle />
              <PortableCompletionSOA />
              {showGauges && (
                <PanelGauges />
              )}
            </React.Fragment>
          )}
      </div>
    </div>
  );
};

const styles = css`
  background-color: rgba(58,66,77,.1);
  border-top: 1px solid #e4e7ec;
  height: calc(100vh - ${l1NavHeight}px);
  top: ${l1NavHeight}px;
  left: 0px;
  overflow-y: auto;
  position: absolute;
  width: 100%;
  z-index: 1011;

  .completion-criteria-panel-wrapper {
    background-color: #fff;
  }
`;

const PortableCompletionPanel = () => {
  const dispatch = useAppDispatch();

  const portableCompletionPanelSelectedTab: CompletionCriteriaType = useSelector((state: RootState) => state.app.timeline.portableCompletionPanelSelectedTab);
  const currentCourse: CombinedCourse = useSelector(getCurrentCourse);
  const catalogId = currentCourse?.catalogId;

  useEffect(() => () => {
    // Reset the tab selected
    dispatch(selectPortableCompletionPanelTab(null));
    // Clear all the data
    dispatch(resetPortableCompletionPanelData(catalogId));
  }, [dispatch, catalogId]);

  return (
    <div css={styles}>
      <div className='completion-criteria-panel-wrapper'>
        <PortableCompletionHeader />
        {portableCompletionPanelSelectedTab && (
          <PanelContent />
        )}
      </div>
    </div>
  );
};

export default PortableCompletionPanel;
